<template>
  <div class="report-preview-page">
    <!-- 头部信息栏 -->
    <div class="preview-header">
      <div class="header-content">
        <div class="report-info">
          <h1 class="report-title">{{ reportInfo.reportNumber }}</h1>
        </div>
        
        <div class="header-actions">
          <div class="qr-code">
            <div class="qr-code-container">
              <canvas ref="qrCanvas" width="100" height="100" style="display: block;"></canvas>
              <img ref="qrImage" style="display: none; width: 100px; height: 100px;" alt="二维码" />
            </div>
            <p class="qr-tip">扫码分享</p>
          </div>
          
          <!-- 移除所有操作按钮 -->
          <div class="action-buttons" style="display: none;">
          </div>
        </div>
      </div>
    </div>

    <!-- PDF预览区域 -->
    <div class="pdf-container" v-loading="loading">
      <!-- 桌面端和移动端统一：不分页，展示所有页面 -->
      <div 
        v-if="pdfUrl && !loading && !error" 
        class="pdf-viewer-container all-pages-mode"
        @contextmenu.prevent="handleRightClick"
      >
        <!-- 控制栏 -->
        <div class="pdf-controls">
          <span class="page-info">共 {{ totalPages }} 页</span>
          
          <el-button 
            size="small" 
            icon="el-icon-zoom-in"
            @click="zoomIn"
          >
            放大
          </el-button>
          
          <el-button 
            size="small" 
            icon="el-icon-zoom-out"
            @click="zoomOut"
          >
            缩小
          </el-button>
          
          <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        </div>
        
        <!-- 所有页面画布容器 -->
        <div class="pdf-all-pages-container" ref="pdfAllPagesContainer">
          <div 
            v-for="page in totalPages" 
            :key="page"
            class="pdf-page-wrapper"
          >
            <div class="page-number-label">第 {{ page }} 页</div>
            <canvas 
              :ref="`pdfCanvas${page}`" 
              class="pdf-canvas"
              @contextmenu.prevent="handleRightClick"
            ></canvas>
          </div>
        </div>
      </div>
      
      <div v-else-if="!loading && error" class="error-message">
        <div class="error-content">
          <i class="el-icon-warning"></i>
          <h3>预览失败</h3>
          <p>{{ error }}</p>
          <div class="error-actions">
            <el-button type="primary" @click="retryLoad">重新加载</el-button>
            <!-- 下载功能已移除，仅限在线预览 -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from '@/config'
import request from '@/utils/request'

export default {
  name: 'ReportPreview',
  data() {
    return {
      loading: true,
      downloading: false,
      error: null,
      pdfUrl: '',
      hasShownRightClickTip: false,
      reportInfo: {
        reportNumber: '',
        reportId: null
      },
      // PDF.js相关
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.5,
      rendering: false
    }
  },
  
  computed: {
    reportNumber() {
      return this.$route.query.reportNumber
    },
    
    // 检测是否为移动端设备
    isMobile() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      
      // 检测移动设备
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
      const isSmallScreen = window.innerWidth <= 768
      
      return mobileRegex.test(userAgent) || isSmallScreen
    }
  },
  
  async mounted() {
    await this.initPreview()
    this.generateQRCode()
    this.setupProtection()
  },
  
  beforeDestroy() {
    // 清理事件监听器
    document.removeEventListener('contextmenu', this.preventContextMenu)
    document.removeEventListener('selectstart', this.preventSelection)
    document.removeEventListener('copy', this.preventCopy)
    document.removeEventListener('keydown', this.preventPrint)
    document.removeEventListener('keydown', this.preventSave)
    document.removeEventListener('keydown', this.preventDevTools)
  },
  
  methods: {
    // 初始化预览
    async initPreview() {
      try {
        // 从路由参数获取报告编号
        const reportNumber = this.$route.query.reportNumber
        
        if (!reportNumber) {
          this.error = '缺少报告编号参数'
          this.loading = false
          return
        }
        
        this.reportInfo.reportNumber = reportNumber
        
        // 设置页面标题
        document.title = `报告预览 - ${reportNumber}`
        
        // 通过报告编号查询报告信息
        console.log('📋 通过报告编号查询:', reportNumber)
        const response = await request.get(config.api.report.search, {
          params: {
            report_number: reportNumber,
            page: 1,
            pageSize: 1
          }
        })
        
        console.log('📦 查询响应:', response.data)
        
        if (response.data.code === 200 && response.data.data.list && response.data.data.list.length > 0) {
          const report = response.data.data.list[0]
          this.reportInfo.reportId = report.id
          
          console.log('✅ 找到报告，ID:', report.id)
          
          // 构建PDF预览URL - 使用后端API域名
          this.pdfUrl = `${config.baseURL}/api/reports/${report.id}/preview`
          
          // 先关闭loading，让DOM渲染出来
          this.loading = false
          
          // 桌面端和移动端都加载PDF.js
          // 等待下一个tick，确保Canvas已渲染
          await this.$nextTick()
          await this.loadPDF()
        } else {
          console.error('❌ 未找到报告')
          this.error = '未找到该报告，请检查报告编号是否正确'
          this.loading = false
        }
        
      } catch (error) {
        console.error('初始化预览失败:', error)
        this.error = '预览初始化失败，请稍后重试'
        this.loading = false
      }
    },
    
    // 加载PDF文档
    async loadPDF() {
      try {
        console.log('📄 开始加载PDF...')
        
        // 动态导入PDF.js
        const pdfjsLib = await import('pdfjs-dist')
        
        // 设置worker路径 - 使用CDN方式更可靠
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`
        
        console.log('✅ PDF.js库加载成功')
        console.log('📡 PDF URL:', this.pdfUrl)
        
        // 加载PDF文档
        const loadingTask = pdfjsLib.getDocument({
          url: this.pdfUrl,
          withCredentials: false
        })
        
        this.pdfDoc = await loadingTask.promise
        this.totalPages = this.pdfDoc.numPages
        
        console.log(`✅ PDF加载成功，共 ${this.totalPages} 页`)
        
        // 等待DOM更新后再渲染
        await this.$nextTick()
        
        // 桌面端和移动端统一：渲染所有页面
        console.log('📄 开始渲染所有页面...')
        await this.renderAllPages()
        
      } catch (error) {
        console.error('❌ PDF加载失败:', error)
        console.error('错误详情:', error.message)
        this.pdfUrl = ''
        if (error?.name === 'UnexpectedResponseException' && error?.status === 429) {
          this.error = '访问次数过多，已被临时限制，请稍后再试。'
        } else {
          this.error = `PDF文件加载失败: ${error.message || '请稍后重试'}`
        }
        this.loading = false
      }
    },
    
    // 渲染所有页面（桌面端和移动端）
    async renderAllPages() {
      if (this.rendering) {
        console.log('⚠️ 正在渲染中，跳过本次请求')
        return
      }
      
      try {
        this.rendering = true
        console.log(`🎨 开始渲染所有 ${this.totalPages} 页...`)
        
        if (!this.pdfDoc) {
          throw new Error('PDF文档未加载')
        }
        
        // 渲染每一页
        for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
          const page = await this.pdfDoc.getPage(pageNum)
          
          // 获取canvas引用（通过动态ref）
          const canvasArray = this.$refs[`pdfCanvas${pageNum}`]
          const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray
          
          if (!canvas) {
            console.warn(`⚠️ 第 ${pageNum} 页的Canvas未找到`)
            continue
          }
          
          const context = canvas.getContext('2d')
          const viewport = page.getViewport({ scale: this.scale })
          
          // 设置canvas尺寸
          canvas.width = viewport.width
          canvas.height = viewport.height
          
          // 渲染PDF页面到canvas
          const renderContext = {
            canvasContext: context,
            viewport: viewport
          }
          
          await page.render(renderContext).promise
          console.log(`✅ 第 ${pageNum} 页渲染完成`)
        }
        
        this.rendering = false
        console.log(`✅ 所有页面渲染完成`)
        
      } catch (error) {
        console.error('❌ 页面渲染失败:', error)
        console.error('错误详情:', error.message)
        this.rendering = false
        this.$message.error(`页面渲染失败: ${error.message}`)
      }
    },
    
    // 渲染指定页面（桌面端）
    async renderPage(pageNum) {
      if (this.rendering) {
        console.log('⚠️ 正在渲染中，跳过本次请求')
        return
      }
      
      try {
        this.rendering = true
        console.log(`🎨 开始渲染第 ${pageNum} 页...`)
        
        // 检查pdfDoc是否存在
        if (!this.pdfDoc) {
          throw new Error('PDF文档未加载')
        }
        
        const page = await this.pdfDoc.getPage(pageNum)
        const canvas = this.$refs.pdfCanvas
        
        if (!canvas) {
          throw new Error('Canvas元素未找到')
        }
        
        const context = canvas.getContext('2d')
        
        // 计算缩放比例
        const viewport = page.getViewport({ scale: this.scale })
        
        console.log(`📐 页面尺寸: ${viewport.width} x ${viewport.height}`)
        
        // 设置canvas尺寸
        canvas.width = viewport.width
        canvas.height = viewport.height
        
        // 渲染PDF页面到canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        }
        
        await page.render(renderContext).promise
        
        this.currentPage = pageNum
        this.rendering = false
        
        console.log(`✅ 第 ${pageNum} 页渲染完成`)
        
      } catch (error) {
        console.error('❌ 页面渲染失败:', error)
        console.error('错误详情:', error.message)
        this.rendering = false
        this.$message.error(`页面渲染失败: ${error.message}`)
      }
    },
    
    // 上一页
    async prevPage() {
      if (this.currentPage <= 1) return
      await this.renderPage(this.currentPage - 1)
    },
    
    // 下一页
    async nextPage() {
      if (this.currentPage >= this.totalPages) return
      await this.renderPage(this.currentPage + 1)
    },
    
    // 放大
    async zoomIn() {
      this.scale += 0.25
      await this.renderAllPages()
    },
    
    // 缩小
    async zoomOut() {
      if (this.scale <= 0.5) return
      this.scale -= 0.25
      await this.renderAllPages()
    },
    
    // 处理右键点击
    handleRightClick(e) {
      e.preventDefault()
      e.stopPropagation()
      // 只在第一次右键时提示，避免过于频繁
      if (!this.hasShownRightClickTip) {
        this.$message.warning('为保护文档版权，已禁用右键菜单')
        this.hasShownRightClickTip = true
      }
      return false
    },
    
    // 重新加载
    retryLoad() {
      this.loading = true
      this.error = null
      setTimeout(() => {
        this.initPreview()
      }, 1000)
    },
    
    // 设置页面保护（禁用右键、复制等）
    setupProtection() {
      // 禁用右键菜单
      document.addEventListener('contextmenu', this.preventContextMenu)
      
      // 禁用选择文本
      document.addEventListener('selectstart', this.preventSelection)
      
      // 禁用复制
      document.addEventListener('copy', this.preventCopy)
      
      // 禁用打印快捷键 (Ctrl+P)
      document.addEventListener('keydown', this.preventPrint)
      
      // 禁用保存快捷键 (Ctrl+S)
      document.addEventListener('keydown', this.preventSave)
      
      // 禁用F12开发者工具
      document.addEventListener('keydown', this.preventDevTools)
      
      console.log('✅ 页面保护已启用')
    },
    
    // 阻止右键菜单
    preventContextMenu(e) {
      e.preventDefault()
      return false
    },
    
    // 阻止文本选择
    preventSelection(e) {
      e.preventDefault()
      return false
    },
    
    // 阻止复制
    preventCopy(e) {
      e.preventDefault()
      return false
    },
    
    // 阻止打印
    preventPrint(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault()
        this.$message.warning('打印功能已禁用')
        return false
      }
    },
    
    // 阻止保存
    preventSave(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        this.$message.warning('保存功能已禁用')
        return false
      }
    },
    
    // 阻止开发者工具（F12）
    preventDevTools(e) {
      if (e.key === 'F12') {
        e.preventDefault()
        return false
      }
    },
    
    // 关闭页面
    closePage() {
      window.close()
    },
    
    // 生成二维码
    generateQRCode() {
      console.log('🔄 开始生成二维码...')
      
      // 延迟执行，确保DOM已渲染
      setTimeout(() => {
        const currentUrl = window.location.href
        console.log('📱 当前URL:', currentUrl)
        
        // 优先尝试img方式（更稳定）
        this.generateQRCodeByDataURL(currentUrl)
      }, 300)
    },
    
    // 使用Data URL方式生成二维码（更稳定）
    async generateQRCodeByDataURL(url) {
      try {
        console.log('📸 使用Data URL方式生成二维码...')
        
        // 动态导入qrcode库
        const QRCode = await import('qrcode')
        
        const qrDataUrl = await QRCode.default.toDataURL(url, {
          width: 100,
          height: 100,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        
        const canvas = this.$refs.qrCanvas
        const img = this.$refs.qrImage
        
        if (img) {
          img.src = qrDataUrl
          img.style.display = 'block'
          if (canvas) canvas.style.display = 'none'
          console.log('✅ 二维码生成成功!')
          return true
        }
      } catch (error) {
        console.error('❌ Data URL方式失败:', error)
      }
      
      // 尝试Canvas方式作为备用
      return this.generateQRCodeByCanvas(url)
    },
    
    // Canvas方式生成二维码（备用）
    async generateQRCodeByCanvas(url) {
      try {
        console.log('🎨 尝试Canvas方式生成二维码...')
        
        const canvas = this.$refs.qrCanvas
        if (!canvas) {
          console.warn('Canvas元素未找到')
          return false
        }
        
        // 动态导入qrcode库
        const QRCode = await import('qrcode')
        
        await QRCode.default.toCanvas(canvas, url, {
          width: 100,
          height: 100,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        
        // 显示canvas，隐藏img
        canvas.style.display = 'block'
        const img = this.$refs.qrImage
        if (img) img.style.display = 'none'
        
        console.log('✅ Canvas方式二维码生成成功!')
        return true
        
      } catch (error) {
        console.error('❌ Canvas方式失败:', error)
        
        // 最后的降级方案
        this.showQRCodeFallback()
        return false
      }
    },
    
    // 降级显示方案
    showQRCodeFallback() {
      console.log('📝 显示降级方案...')
      const canvas = this.$refs.qrCanvas
      if (canvas) {
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#f0f2f5'
        ctx.fillRect(0, 0, 100, 100)
        ctx.strokeStyle = '#d9d9d9'
        ctx.strokeRect(0, 0, 100, 100)
        ctx.fillStyle = '#666666'
        ctx.font = '14px Arial'
        ctx.textAlign = 'center'
        ctx.fillText('📱', 50, 35)
        ctx.font = '12px Arial'
        ctx.fillText('扫码功能', 50, 58)
        ctx.fillText('暂不可用', 50, 75)
        canvas.style.display = 'block'
      }
    }
  }
}
</script>

<style scoped>
/* 禁用整个页面的文本选择和右键 */
.report-preview-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.preview-header {
  background: white;
  border-bottom: 1px solid #e4e7ed;
  padding: 12px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.report-info {
  flex: 1;
}

.report-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}


.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.qr-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.qr-code-container {
  padding: 5px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e4e7ed;
}

.qr-tip {
  margin: 0;
  font-size: 12px;
  color: #909399;
  text-align: center;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-buttons .el-button {
  min-width: 120px;
}

.pdf-container {
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  /* 禁用PDF容器的文本选择 */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* 禁用拖拽 */
  -webkit-user-drag: none;
  -moz-user-drag: none;
  user-drag: none;
}

/* PDF.js查看器容器 */
.pdf-viewer-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

/* 不分页模式（桌面端和移动端统一） */
.pdf-viewer-container.all-pages-mode {
  height: auto;
  min-height: 100%;
}

/* PDF控制栏 */
.pdf-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  flex-wrap: wrap;
}

.page-info {
  font-size: 14px;
  color: #606266;
  margin: 0 8px;
  white-space: nowrap;
}

.zoom-info {
  font-size: 14px;
  color: #606266;
  margin-left: 8px;
  white-space: nowrap;
}

/* PDF画布容器 */
.pdf-canvas-container {
  flex: 1;
  overflow: auto;
  background: #e4e7ed;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
}

/* 移动端所有页面容器 */
.pdf-all-pages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: #e4e7ed;
  padding: 10px;
}

/* 单个页面包装器 */
.pdf-page-wrapper {
  margin-bottom: 20px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.pdf-page-wrapper:last-child {
  margin-bottom: 0;
}

/* 页码标签 */
.page-number-label {
  background: #f5f7fa;
  padding: 8px 12px;
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  border-bottom: 1px solid #e4e7ed;
  text-align: center;
}


/* PDF画布 */
.pdf-canvas {
  display: block;
  background: white;
  max-width: 100%;
  height: auto;
  cursor: default;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 桌面端PDF画布样式 */
.pdf-canvas-container .pdf-canvas {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

/* 移动端所有页面容器中的画布 */
.pdf-all-pages-container .pdf-canvas {
  width: 100%;
  height: auto;
  box-shadow: none;
}

.error-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-content i {
  font-size: 48px;
  color: #f56c6c;
  margin-bottom: 16px;
}

.error-content h3 {
  margin: 0 0 12px 0;
  color: #303133;
}

.error-content p {
  margin: 0 0 20px 0;
  color: #606266;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: space-between;
    gap: 12px;
  }
  
  .action-buttons {
    flex-direction: row;
  }
  
  .action-buttons .el-button {
    min-width: auto;
    flex: 1;
  }
  
  .report-title {
    font-size: 20px;
  }
  
  .pdf-container {
    padding: 10px;
  }
  
  /* PDF控制栏移动端优化 */
  .pdf-controls {
    padding: 8px 10px;
    gap: 6px;
    justify-content: center;
  }
  
  .pdf-controls .el-button {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .page-info,
  .zoom-info {
    font-size: 11px;
    margin: 0 4px;
  }
  
  .pdf-canvas-container {
    padding: 10px;
  }
  
  /* 移动端PDF容器优化 */
  .pdf-all-pages-container {
    padding: 8px;
  }
  
  .pdf-page-wrapper {
    margin-bottom: 15px;
  }
  
  .page-number-label {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  /* 移动端PDF画布优化 */
  .pdf-canvas {
    max-width: 100%;
    width: 100% !important;
    height: auto !important;
  }
}

@media (max-width: 480px) {
  .preview-header {
    padding: 10px 15px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .qr-code-container {
    padding: 4px;
  }
  
  .qr-code-container canvas,
  .qr-code-container img {
    width: 90px !important;
    height: 90px !important;
  }
  
  /* 小屏幕PDF控制栏进一步优化 */
  .pdf-controls {
    padding: 6px 8px;
    gap: 4px;
    flex-wrap: wrap;
  }
  
  .pdf-controls .el-button {
    padding: 5px 8px;
    font-size: 11px;
  }
  
  .pdf-controls .el-button span {
    display: none;
  }
  
  .pdf-controls .el-button i {
    margin: 0;
  }
  
  .page-info,
  .zoom-info {
    font-size: 10px;
    margin: 0 2px;
  }
  
  .pdf-canvas-container {
    padding: 5px;
  }
  
  /* 小屏幕移动端容器优化 */
  .pdf-all-pages-container {
    padding: 5px;
  }
  
  .pdf-page-wrapper {
    margin-bottom: 10px;
    border-radius: 3px;
  }
  
  .page-number-label {
    padding: 5px 8px;
    font-size: 11px;
  }
  
  .error-actions {
    flex-direction: column;
  }
  
  .error-actions .el-button {
    width: 100%;
  }
}
</style>
